import { Button } from "antd";
import { AiFillDelete } from "react-icons/ai";
import { LuDelete, LuFileEdit } from "react-icons/lu";
import CustomerLink from "../shared/CustomerLink";
import RiskBadge from "../shared/RiskBadge";
import VerifyBvnButton from "./VerifyBvnButton";
import AdminStatusButton from "../settings/roles_permission/AdminStatusButton";

const usd = (n) =>
  `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const columns = (navigate) => [
  {
    title: "First Name",
    dataIndex: "firstName",
    key: "firstName",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Last Name",
    dataIndex: "lastName",
    key: "lastName",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Email Address",
    dataIndex: "email",
    key: "email",
    render: (value) => <p className="lowercase">{value}</p>,
  },
  {
    title: "Phone Number",
    dataIndex: "phoneNumber",
    key: "phoneNumber",
    render: (value, record) => (
      <p className="capitalize">
        {value}{" "}
        {record.isPhoneVerified ? (
          <span className="text-[#219653] text-[12px]" title="Phone verified">✓</span>
        ) : (
          <span className="text-[#A0A3BD] text-[12px]" title="Phone not verified">unverified</span>
        )}
      </p>
    ),
  },
  {
    title: "BVN",
    dataIndex: "isBvnVerified",
    key: "bvn",
    render: (verified, record) =>
      verified ? (
        <span className="text-[#219653] text-[13px]">Verified</span>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-[#A0A3BD] text-[13px] whitespace-nowrap">
            {record.hasBvn ? "Not verified" : "No BVN"}
          </span>
          <VerifyBvnButton
            size="small"
            userId={record._id}
            name={[record.firstName, record.lastName].filter(Boolean).join(" ")}
            hasBvn={record.hasBvn}
          />
        </div>
      ),
  },
  {
    title: "Risk",
    dataIndex: "risk",
    key: "risk",
    render: (risk, record) => (
      <div className="flex items-center gap-2">
        <RiskBadge risk={risk} />
        {record.watchlistFlag?.flagged && (
          <span className="bg-[#EB5757] text-white rounded-[3px] px-[6px] py-[2px] text-[11px] whitespace-nowrap">
            BVN watchlist
          </span>
        )}
        {record.duplicateBvnFlag?.flagged && (
          <span className="bg-[#F2994A] text-white rounded-[3px] px-[6px] py-[2px] text-[11px] whitespace-nowrap">
            Duplicate BVN
          </span>
        )}
      </div>
    ),
  },
  {
    title: "Action",
    key: "action",
    render: (_, values) => (
      <Button
        type="primary"
        onClick={() => navigate(`/customers/${values._id}`)}
      >
        View
      </Button>
    ),
  },
];

export const orderColumn = [
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Price",
    dataIndex: "price",
    key: "price",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Total Price",
    dataIndex: "totalPrice",
    key: "totalPrice",
    render: (value) => <p className="capitalize">{value}</p>,
  },
];

export const adminColumn = [
  {
    title: "User",
    dataIndex: "id",
    key: "id",
    render: (_, data) => (
      <div>
        <div className="flex gap-5">
          <div className="w-[42px] h-[42px] flex justify-center items-center border-2 border-dark rounded-full">
            <p className="text-center font-bold">
              {data?.firstName?.charAt(0)}
              {data?.lastName?.charAt(0)}
            </p>
          </div>
          <div>
            <p className="capitalize">
              {data?.firstName} {data?.lastName}
            </p>
            <span className="text-xs">{data?.email}</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    title: "Role",
    dataIndex: "roleId",
    key: "role",
    render: (value) => <p className="capitalize">{value?.title}</p>,
  },
  {
    title: "Date Added",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (
      <p className="capitalize">{new Date(value).toLocaleDateString()}</p>
    ),
  },
  {
    title: "Status",
    dataIndex: "isActive",
    key: "isActive",
    render: (value) => (
      <p className="capitalize">{value !== false ? "Active" : "Inactive"}</p>
    ),
  },
  {
    title: "Action",
    key: "action",
    render: (_, data) => <AdminStatusButton admin={data} />,
  },
];

export const transactionColumn = [
  {
    title: "Type",
    dataIndex: "metadata",
    key: "metadata",
    render: (value) => <p className="capitalize">{value?.transactionType}</p>,
  },
  {
    title: "Transaction ID",
    dataIndex: "_id",
    key: "_id",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Beneficiary",
    dataIndex: "metadata",
    key: "metadata",
    render: (value) => (
      <p className="uppercase">{value?.description || value?.coin}</p>
    ),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (value, data) => (
      <p className="capitalize">
        {data?.coin
          ? new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 8,
            }).format(Number(data?.metadata?.coinQty || 0))
          : Number(data?.amount).toLocaleString()}
      </p>
    ),
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (
      <p className="capitalize">{new Date(value).toLocaleDateString()}</p>
    ),
  },
];

export const cryptoTradeColumn = [
  {
    title: "Type",
    dataIndex: "crytoType",
    key: "crytoType",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Order ID",
    dataIndex: "_id",
    key: "_id",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Customer",
    dataIndex: "id",
    key: "id",
    render: (_value, record) => <CustomerLink record={record} />,
  },
  {
    title: "Crypto",
    dataIndex: "coin",
    key: "coin",
    render: (value) => (
      <p className="uppercase">
        {value.includes(":") ? value.split(":")[1] : value}
      </p>
    ),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (value, record) => {
      const dollars = record.usdAtTrade ?? record.usdNow;
      return (
        <p className="uppercase whitespace-nowrap">
          {Number(value)}
          {dollars != null && <span className="text-[#00000080]"> ({usd(dollars)})</span>}
        </p>
      );
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (value) => <p className="capitalize">{value}</p>,
  },
];

export const giftCardTradeColumn = [
  {
    title: "Type",
    dataIndex: "metadata",
    key: "metadata",
    render: (value) => <p className="capitalize">{value?.transactionType}</p>,
  },
  {
    title: "Order ID",
    dataIndex: "_id",
    key: "_id",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (
      <p className="capitalize">{new Date(value).toLocaleDateString()}</p>
    ),
  },
  {
    title: "Customer",
    dataIndex: "user",
    key: "user",
    render: (_value, record) => (
      <CustomerLink record={record} className="lowercase">
        {record?.user?.email}
      </CustomerLink>
    ),
  },
  {
    title: "Brand",
    dataIndex: "brandName",
    key: "brandName",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Amount",
    dataIndex: "amount",
    key: "amount",
    render: (value) => (
      <p className="uppercase">{Number(value).toLocaleString()}</p>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (value) => <p className="capitalize">{value}</p>,
  },
];
// customer name, category, card amount, quantity, payout amount
export const giftCardRequestColumn = (
  mutation,
  setId,
  setOpen,
  setOpenDetail,
  setSingleData,
  getModulePermission,
) => [
  {
    title: "Customer Name",
    render: (_value, record) => <CustomerLink record={record} />,
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Card Amount",
    dataIndex: "amount",
    key: "amount",
    render: (value) => (
      <p className="capitalize">{Number(value).toLocaleString()}</p>
    ),
  },
  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    render: (value) => (
      <p className="capitalize">{Number(value || 0).toLocaleString()}</p>
    ),
  },
  {
    title: "Brand",
    dataIndex: "receiptType",
    key: "receiptType",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Payout Amount",
    dataIndex: "totalAmountInNaira",
    key: "totalAmountInNaira",
    render: (value) => (
      <p className="capitalize">₦{Number(value || 0).toLocaleString()}</p>
    ),
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (
      <p className="capitalize">{new Date(value).toLocaleDateString()}{" "}{new Date(value).toLocaleTimeString()}</p>
    ),
  },
  {
    title: "Actions",
    render: (_value, record) => (
      <div className="flex gap-5">
        {getModulePermission("trade", "canTreat") && (
          <>
            <Button
              type="primary"
              loading={mutation.isLoading}
              onClick={() => {
                mutation.mutate({ status: "processing", id: record._id });
              }}
            >
              Accept
            </Button>

            <Button
              type="default"
              className="bg-red-800 text-white"
              disabled={mutation.isLoading}
              onClick={() => {
                setOpen(true);
                setId(record._id);
              }}
            >
              Decline
            </Button>

            <Button
              type="default"
              disabled={mutation.isLoading}
              onClick={() => {
                setSingleData(record || {});
                setOpenDetail(true);
              }}
            >
              View
            </Button>
          </>
        )}
      </div>
    ),
  },
];

export const giftCardRequestHistoryColumn = [
  {
    title: "Customer Name",
    render: (_value, record) => <CustomerLink record={record} />,
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Card Amount",
    dataIndex: "amount",
    key: "amount",
    render: (value) => (
      <p className="capitalize">{Number(value).toLocaleString()}</p>
    ),
  },
  {
    title: "Quantity",
    dataIndex: "quantity",
    key: "quantity",
    render: (value) => (
      <p className="capitalize">{Number(value || 0).toLocaleString()}</p>
    ),
  },
  {
    title: "Brand",
    dataIndex: "receiptType",
    key: "receiptType",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Payout Amount",
    dataIndex: "totalAmountInNaira",
    key: "totalAmountInNaira",
    render: (value) => (
      <p className="capitalize">₦{Number(value || 0).toLocaleString()}</p>
    ),
  },
  {
    title: "Date",
    dataIndex: "createdAt",
    key: "createdAt",
    render: (value) => (
      <p className="capitalize">{new Date(value).toLocaleDateString()}</p>
    ),
  },
];

export const shippingColumn = (setData, setOpen, setoOpenDeleteModal) => [
  {
    title: "Product",
    dataIndex: "product",
    key: "product",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Address",
    dataIndex: "state",
    key: "state",
    render: (value) => {
      const add = JSON.parse(value);
      return (
        <div className="flex flex-col gap-4">
          <p className="capitalize">
            {add.line1} {add.city}, {add.state}, {add.country}
          </p>
        </div>
      );
    },
  },
  {
    title: "Weight",
    dataIndex: "weight",
    key: "range",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Rate",
    dataIndex: "rate",
    key: "rate",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Action",
    render: (_, values) => (
      <div className="flex gap-5">
        <LuFileEdit
          className="text-[28px] cursor-pointer text-center pr-2"
          onClick={() => {
            setData(values);
            setOpen(true);
          }}
        />

        <AiFillDelete
          className="text-[28px] cursor-pointer text-center pr-2"
          onClick={() => {
            setData(values);
            setoOpenDeleteModal(true);
          }}
        />
      </div>
    ),
  },
];

export const giftCardColumn = (setData, setOpen, setOpenDeleteModal, getModulePermission) => [
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Range",
    dataIndex: "range",
    key: "range",
    render: (_, values) => (
      <p className="capitalize">
        {values.range.min} - {values.range.max}
      </p>
    ),
  },
  {
    title: "Rate",
    dataIndex: "rate",
    key: "rate",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Action",
    render: (_, values) => (
      <div className="flex gap-5">
        {getModulePermission("rate", "canUpdate") && (
          <LuFileEdit
            className="text-[28px] cursor-pointer text-center pr-2"
            onClick={() => {
              setData(values);
              setOpen(true);
            }}
          />
        )}

        {getModulePermission("rate", "canDelete") && (
          <AiFillDelete
            className="text-[28px] cursor-pointer text-center pr-2"
            onClick={() => {
              setData(values);
              setOpenDeleteModal(true);
            }}
          />
        )}
      </div>
    ),
  },
];

export const cryptoColumn = (
  setData,
  setOpen,
  setOpenDeleteModal,
  getModulePermission,
) => [
  {
    title: "Range",
    dataIndex: "range",
    key: "range",
    render: (_, values) => (
      <p className="capitalize">
        {values.range.min} - {values.range.max}
      </p>
    ),
  },
  {
    title: "Rate",
    dataIndex: "rate",
    key: "rate",
    render: (value) => <p className="capitalize">{value}</p>,
  },
  {
    title: "Action",
    render: (_, values) => (
      <div className="flex gap-5">
        {getModulePermission("rate", "canUpdate") && (
          <LuFileEdit
            className="text-[28px] cursor-pointer text-center pr-2"
            onClick={() => {
              setData(values);
              setOpen(true);
            }}
          />
        )}
        {getModulePermission("rate", "canDelete") && (
          <AiFillDelete
            className="text-[28px] cursor-pointer text-center pr-2"
            onClick={() => {
              setData(values);
              setOpenDeleteModal(true);
            }}
          />
        )}
      </div>
    ),
  },
];
